<template>
  <div class="page-container">
    <el-card>
      <div class="search-bar">
        <el-input v-model="query.keyword" placeholder="搜索角色名称/标识" clearable style="width: 220px" @keyup.enter.native="handleSearch" />
        <el-select v-model="query.status" placeholder="角色状态" clearable style="width: 120px">
          <el-option label="启用" :value="1" />
          <el-option label="禁用" :value="0" />
        </el-select>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <div class="action-bar">
        <el-button v-permission="['system:role:create']" type="success" size="mini" icon="el-icon-plus" @click="handleCreate">新建</el-button>
      </div>

      <el-table :data="tableData" v-loading="loading" border stripe style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column prop="name" label="角色名称" width="150" />
        <el-table-column prop="code" label="角色标识" width="150" />
        <el-table-column prop="description" label="描述" min-width="180" />
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" min-width="160" />
        <el-table-column label="操作" width="220" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-tooltip :disabled="row.code !== 'super_admin'" content="超级管理员角色不允许编辑" placement="top">
              <span>
                <el-button v-permission="['system:role:update']" :disabled="row.code === 'super_admin'" type="primary" size="mini" icon="el-icon-edit" @click="handleEdit(row)">编辑</el-button>
              </span>
            </el-tooltip>
            <el-tooltip :disabled="row.code !== 'super_admin'" content="超级管理员角色不允许修改权限" placement="top">
              <span>
                <el-button v-permission="['system:role:assign']" :disabled="row.code === 'super_admin'" type="warning" size="mini" icon="el-icon-menu" @click="handleAssignMenu(row)">权限</el-button>
              </span>
            </el-tooltip>
            <el-tooltip :disabled="row.code !== 'super_admin'" content="超级管理员角色不允许删除" placement="top">
              <span>
                <el-button v-permission="['system:role:delete']" :disabled="row.code === 'super_admin'" type="danger" size="mini" icon="el-icon-delete" @click="handleDelete(row)">删除</el-button>
              </span>
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        :current-page="query.page"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top: 16px; text-align: right"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialog.title" :visible.sync="dialog.visible" width="480px" @close="resetForm">
      <el-form ref="roleForm" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="角色名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入角色名称" />
        </el-form-item>
        <el-form-item label="角色标识" prop="code">
          <el-input v-model="form.code" :disabled="!!form.id" placeholder="英文标识，如: admin" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="请输入角色描述" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="mini" @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="dialog.loading" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>

    <!-- 分配菜单弹窗 -->
    <el-dialog title="分配菜单权限" :visible.sync="menuDialog.visible" width="500px">
      <p class="menu-role-info">角色：<strong>{{ menuDialog.role.name }}</strong></p>
      <el-tree
        ref="menuTree"
        :data="menuTree"
        :props="{ label: 'title', children: 'children' }"
        show-checkbox
        node-key="id"
        :default-checked-keys="menuDialog.checkedKeys"
        :default-expand-all="true"
      >
        <span slot-scope="{ node, data }">
          <i :class="data.icon || 'el-icon-document'" style="margin-right: 4px" />
          {{ node.label }}
          <el-tag v-if="data.type === 2" size="mini" type="info" style="margin-left: 4px">按钮</el-tag>
        </span>
      </el-tree>
      <div slot="footer">
        <el-button size="mini" @click="menuDialog.visible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="menuDialog.loading" @click="handleMenuSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getRoleList, createRole, updateRole, deleteRole, assignRoleMenu, getRoleMenus } from '@/api/role'
import { getMenuList } from '@/api/menu'

export default {
  name: 'RoleManage',
  data() {
    return {
      loading: false,
      tableData: [],
      total: 0,
      query: { page: 1, pageSize: 15, keyword: '', status: '' },
      dialog: { visible: false, title: '', loading: false },
      form: { id: null, name: '', code: '', description: '', status: 1 },
      rules: {
        name: [{ required: true, message: '角色名称不能为空', trigger: 'blur' }],
        code: [{ required: true, message: '角色标识不能为空', trigger: 'blur' }]
      },
      menuDialog: { visible: false, loading: false, role: {}, checkedKeys: [] },
      menuTree: []
    }
  },
  created() {
    this.fetchData()
    this.fetchMenuTree()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const res = await getRoleList(this.query)
        this.tableData = res.data.list
        this.total = res.data.total
      } finally {
        this.loading = false
      }
    },
    async fetchMenuTree() {
      const res = await getMenuList()
      this.menuTree = this.buildTree(res.data || [])
    },
    buildTree(list, parentId = 0) {
      const children = list.filter(item => item.parent_id === parentId)
      return children.map(item => ({
        ...item,
        children: this.buildTree(list, item.id)
      }))
    },
    handleSearch() { this.query.page = 1; this.fetchData() },
    handleReset() { this.query = { page: 1, pageSize: 15, keyword: '', status: '' }; this.fetchData() },
    handleSizeChange(val) { this.query.pageSize = val; this.fetchData() },
    handleCurrentChange(val) { this.query.page = val; this.fetchData() },
    handleCreate() {
      this.form = { id: null, name: '', code: '', description: '', status: 1 }
      this.dialog = { visible: true, title: '新增角色', loading: false }
    },
    handleEdit(row) {
      this.form = { id: row.id, name: row.name, code: row.code, description: row.description, status: row.status }
      this.dialog = { visible: true, title: '编辑角色', loading: false }
    },
    async handleDelete(row) {
      try {
        await this.$confirm(`确认删除角色「${row.name}」？`, '提示', { type: 'warning' })
        await deleteRole({ id: row.id })
        this.$message.success('删除成功')
        this.fetchData()
      } catch {}
    },
    async handleAssignMenu(row) {
      this.menuDialog.role = row
      this.menuDialog.visible = true
      this.menuDialog.loading = false
      const res = await getRoleMenus({ id: row.id })
      const savedIds = res.data || []
      this.$nextTick(() => {
        if (!this.$refs.menuTree) return
        // el-tree setCheckedKeys 若传入父节点 ID 会自动全选所有子节点，
        // 导致未分配的兄弟菜单被误勾选。
        // 只传"在已分配集合中没有子节点"的叶级 ID，
        // el-tree 会自动推导父节点的半勾/全勾状态。
        this.$refs.menuTree.setCheckedKeys(this.getAssignedLeafIds(savedIds))
      })
    },
    // 从已分配 ID 集合中，找出没有子节点也在集合中的"底层"ID
    getAssignedLeafIds(savedIds) {
      if (!savedIds.length || !this.menuTree.length) return savedIds
      const savedSet = new Set(savedIds)
      const nonLeafIds = new Set()
      const scan = (nodes) => {
        nodes.forEach(node => {
          if (node.children && node.children.length) {
            if (node.children.some(c => savedSet.has(c.id))) {
              nonLeafIds.add(node.id)
            }
            scan(node.children)
          }
        })
      }
      scan(this.menuTree)
      return savedIds.filter(id => !nonLeafIds.has(id))
    },
    async handleMenuSubmit() {
      this.menuDialog.loading = true
      try {
        const checkedKeys = this.$refs.menuTree.getCheckedKeys()
        const halfCheckedKeys = this.$refs.menuTree.getHalfCheckedKeys()
        const menuIds = [...checkedKeys, ...halfCheckedKeys]
        await assignRoleMenu({ id: this.menuDialog.role.id, menuIds })
        this.$message.success('权限分配成功')
        this.menuDialog.visible = false
      } finally {
        this.menuDialog.loading = false
      }
    },
    handleSubmit() {
      this.$refs.roleForm.validate(async valid => {
        if (!valid) return
        this.dialog.loading = true
        try {
          if (this.form.id) {
            await updateRole(this.form)
            this.$message.success('更新成功')
          } else {
            await createRole(this.form)
            this.$message.success('创建成功')
          }
          this.dialog.visible = false
          this.fetchData()
        } finally {
          this.dialog.loading = false
        }
      })
    },
    resetForm() {
      this.$refs.roleForm && this.$refs.roleForm.resetFields()
    }
  }
}
</script>

<style lang="scss" scoped>
.menu-role-info {
  margin: 0 0 16px;
  color: #606266;
  font-size: 13px;
}
</style>
